"use client";

import { useEffect, useState } from "react";
import { useCookieConsentContext } from "@use-cookie-consent/react";
import Link from "next/link";
import CustomButton from "@/components/customButton/page";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { initSentry, closeSentry } from "../../../sentry.client.config";

export function CookieConsentBanner() {
    const { consent, declineAllCookies, acceptCookies } =
        useCookieConsentContext();
    const [showBanner, setShowBanner] = useState(false);
    const [startAnimation, setStartAnimation] = useState(false);

    useEffect(() => {
        // Show banner only if consent for thirdParty cookies hasn't been explicitly given or denied.
        if (consent.thirdParty === true || consent.thirdParty === false) {
            setShowBanner(false);
        } else {
            setShowBanner(true);
        }
    }, [consent]);

    useEffect(() => {
        if (showBanner) {
            const timer = setTimeout(() => {
                setStartAnimation(true);
            }, 100);

            return () => {
                clearTimeout(timer);
            };
        }

        setStartAnimation(false);
    }, [showBanner]);

    const handleAccept = () => {
        acceptCookies({ thirdParty: true });
        initSentry();
    };

    const handleDecline = () => {
        declineAllCookies(); // Or specifically decline analytics: acceptCookies({ analytics: false })
        closeSentry();
    };

    if (!showBanner) {
        return null;
    }

    return (
        <div
            className={cn(
                "fixed bottom-6 left-4 right-4 sm:bottom-6 sm:left-auto sm:right-6 sm:max-w-md z-[100] rounded-[0.5rem] border-t bg-background/90 dark:bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/90 p-3 sm:p-4 text-left",
                "max-w-[calc(100vw-2rem)] sm:max-w-md", // Ensure banner never exceeds viewport width
                "shadow-lg border border-border", // Better visibility
                "transition-opacity duration-500 ease-in-out",
                startAnimation ? "opacity-100" : "opacity-0"
            )}
        >
            <p className="text-xs sm:text-sm text-muted-foreground leading-tight">
                At Story Stream, your stories, drafts, and personal information
                are always kept safe—we never share or sell your data. We use
                Microsoft Clarity and Sentry to understand how our software
                performs and to diagnose issues. This telemetry helps us improve
                the site and is never sold to third parties. You can read more in our{" "}
                <Link
                    href="/docs/general/privacy"
                    className="underline hover:text-primary"
                    target="_blank"
                >
                    Privacy Policy
                </Link>{" "}
                if you&apos;re curious.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center mt-4 sm:mt-6 gap-2 sm:gap-2">
                <CustomButton
                    label="Decline"
                    onClick={handleDecline}
                    bg={false}
                    className="!py-2 !px-4 !text-sm !font-normal !tracking-normal flex-1 sm:flex-initial min-w-0"
                    icon={<ChevronRight className="w-4 h-4" />}
                    iconPosition="right"
                />
                <CustomButton
                    label="Accept"
                    onClick={handleAccept}
                    bg={true}
                    className="!py-2 !px-4 !text-sm !font-normal !tracking-normal !text-white !border-border dark:!border-border flex-1 sm:flex-initial min-w-0"
                    icon={<ChevronRight className="w-4 h-4" />}
                    iconPosition="right"
                />
            </div>
        </div>
    );
}
